import { Button, HorizontalDivider, Icon, Typography } from '@smartgift/frontend-toolkit';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';

import { Card, LinearProgressBar, SubTitle } from '@hero/atoms';
import { DuplicateModal } from '@hero/components/modals';
import { CARDISLE_GREETING_CARD, RECOGNITION_STATUSES, RECOGNITIONS_TAB } from '@hero/constants';
import { DetailItem } from '@hero/molecules';
import { useStartRecognition, useRecogCalculateEstimatedCharge } from '@hero/services';
import { useNotification } from '@hero/store/hooks';
import { getFeatureStatus } from '@hero/utils';
import { formatCurrency, getAddonData, getAnalyticRates } from '@hero/utils/helper';

import {
  isApprovalPending,
  isContactLess,
  isDeactivate,
  isDraftOrReadyToSend,
  isExpired,
  isLive,
  isTeams
} from './constants';
import * as S from './style';

function getForms({ t, isCCPayment }) {
  const recognitionReactivateForm = [
    {
      name: 'expirationDate',
      placeholder: `${t('endDate')}*`,
      type: 'date',
      excludePast: true,
      showTimeSelect: true,
      ...(isCCPayment && { maxDate: dayjs().add(90, 'days').toDate() })
    }
  ];
  return { recognitionReactivateForm };
}

const ProcessingErrorCard = ({ status }) => {
  const { t } = useTranslation();
  const { recognitionCode } = useParams();
  const { mutateAsync: mutateStartRecognition, isLoading: isLoadingStartRecognition } =
    useStartRecognition();

  return (
    <Card>
      <SubTitle title={t('statusSummary')} status={status} style={{ marginBottom: '16px' }} />
      <Typography variant="body2" emphasis="strongest">
        {t('processingErrorDescription')}
      </Typography>

      <HorizontalDivider />

      <S.CardFooter>
        <S.ButtonWrapper>
          <Button
            color="primary"
            variant="outlined"
            onClick={() => mutateStartRecognition(recognitionCode)}
            disabled={isLoadingStartRecognition}
            startIcon={<Icon name="automation" size="sm" />}
          >
            {t('resubmitGift')}
          </Button>
        </S.ButtonWrapper>

        <S.ReactivateText>
          {t('needHelpPlease')}
          <S.LinkButton href="mailto:support@smartgiftit.com">{t('contactSupport')}.</S.LinkButton>
        </S.ReactivateText>
      </S.CardFooter>
    </Card>
  );
};

const ProcessingCard = ({ status, featureType }) => {
  const { t } = useTranslation();

  return (
    <Card>
      <SubTitle title={t('statusSummary')} status={status} style={{ marginBottom: '16px' }} />
      <Typography variant="body2" emphasis="strongest">
        <Trans
          i18nKey="featureProcessingText"
          components={{ b: <b /> }}
          values={{ feature: t(featureType).toLocaleLowerCase() }}
        />
      </Typography>
    </Card>
  );
};

function StatusSummary({
  feature,
  spendingSummary,
  analytics,
  averageAcceptanceRate,
  onDuplicate,
  featureType,
  onArchive,
  onExpire,
  onReactivate,
  onCancelApproval,
  duplicateText,
  isCCPayment,
  isUpdatingRecognition,
  giftCount,
  contactType,
  giftMessage,
  setActiveTab
}) {
  const { showNotification } = useNotification();
  const { t } = useTranslation();
  const { recognitionReactivateForm } = getForms({ t, isCCPayment });
  const status = getFeatureStatus(feature);
  const digitalGiftCard = getAddonData(feature?.addons, CARDISLE_GREETING_CARD);

  const { mutateAsync: calculateRecognitionCharge, isLoading: isCalculationLoading } =
    useRecogCalculateEstimatedCharge();
  const rate = getAnalyticRates(analytics, { format: true });
  const { estimatedCharges } = spendingSummary ?? {};

  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);

  const summaryCard = {
    [RECOGNITION_STATUSES.starting]: <ProcessingCard status={status} featureType={featureType} />,
    [RECOGNITION_STATUSES.error_on_start]: <ProcessingErrorCard status={status} />
  };

  if (summaryCard[status]) {
    return summaryCard[status];
  }

  const onArchiveModal = () => {
    if (isCalculationLoading || isUpdatingRecognition) {
      return;
    }
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      formStyle: {
        padding: '0 50px'
      },
      title: t('archiveFeaturePromptTitle', {
        featureType: t(featureType).toLocaleLowerCase()
      }),
      message: t('areYouSureWantToArchiveDesc', {
        featureType: t(featureType).toLocaleLowerCase()
      }),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      primaryButtonProps: { color: 'error', children: t('archive') },
      secondaryButtonProps: { children: t('dontArchive') },
      onSubmit: onArchive
    });
  };

  const onExpireModal = () => {
    showNotification({
      popupStyle: {
        maxWidth: '500px'
      },
      title: t('areYouSure'),
      message: isCCPayment ? (
        <Trans
          i18nKey="expireRecognitionPromptMessageCC"
          values={{ unOpenedCount: analytics?.total - analytics?.injected }}
          components={{ b: <b /> }}
        />
      ) : (
        <Trans
          i18nKey={
            analytics?.total - analytics?.accepted === 0
              ? 'expireRecognitionPromptMessageNoUnopened'
              : 'expireRecognitionPromptMessageHouseAccount'
          }
          values={{ unOpenedCount: analytics?.total - analytics?.accepted }}
          components={{ b: <b /> }}
        />
      ),
      notificationImage: <Icon name="error" size="xxl" color="var(--color-error-default)" />,
      secondaryButtonProps: { children: t('noKeepActive') },
      primaryButtonProps: { color: 'error', children: t('yesEndIt') },
      onSubmit: onExpire
    });
  };

  const onReactivateModal = async ({ action = 'reactivate' }) => {
    let totalAmount = formatCurrency(spendingSummary?.adjustedEstimatedCharge?.total);
    if (feature?.status?.current === RECOGNITION_STATUSES.expired) {
      const calculatedCharges = await calculateRecognitionCharge({
        params: {
          code: feature?.code
        },
        body: {
          products: feature?.products,
          recipientCount: feature?.numberOfContacts
        }
      });
      totalAmount = formatCurrency(calculatedCharges?.difference?.total);
    }

    const DESCRIPTION = {
      reactivate: t('reactivateModalDescDate'),
      extend: t('extendModalDescDate')
    };
    const CTA = {
      reactivate: 'reactivate',
      extend: 'extendEndDate'
    };

    showNotification({
      notification: isCCPayment && {
        title: t('reactivateNotificationTitle'),
        message: (
          <Trans
            i18nKey="reactivateNotificationMessage"
            values={{ totalAmount }}
            components={{ b: <b /> }}
          />
        ),
        status: 'info'
      },
      notificationImage: <Icon name="calendar" size="xxl" />,
      header: t('reactivateModalTitleDate'),
      message: DESCRIPTION[action],
      formData: recognitionReactivateForm,
      formValidationSchema: recognitionReactivateFormValidationSchema,
      formStyle: { display: 'flex', justifyContent: 'center' },
      primaryButtonProps: { children: t(CTA[action]) },
      secondaryButtonProps: { children: t('cancel') },
      onSubmit: onReactivate
    });
  };

  const statusDescriptions = {
    [RECOGNITION_STATUSES.draft]: <Trans i18nKey="statusSummaryDesc" />,
    [RECOGNITION_STATUSES.readyToSend]: <Trans i18nKey="statusSummaryDesc" />,
    [RECOGNITION_STATUSES.pending_approval]: (
      <Trans
        i18nKey="errors.approvalPendingAlertFeatureName"
        values={{ featureName: t(featureType).toLocaleLowerCase() }}
        components={{ bold: <strong /> }}
      />
    ),
    [RECOGNITION_STATUSES.archived]: <Trans i18nKey="duplicateStatusDescription" />
  };

  return (
    <Card>
      <S.StatusSummaryHeader>
        <SubTitle
          links={
            <Button
              variant="link"
              startIcon={<Icon name="analytics" />}
              onClick={() => setActiveTab(RECOGNITIONS_TAB.analytics)}
            >
              {t('seeAnalytics')}
            </Button>
          }
          title={t('statusSummary')}
          status={status}
        />
        {statusDescriptions[status] && (
          <Typography variant="caption1" color="fg" emphasis="weaker">
            {statusDescriptions[status]}
          </Typography>
        )}
      </S.StatusSummaryHeader>

      <S.StatusWrapper>
        <>
          <DetailItem
            bodyStyle={{
              display: 'flex',
              alignItems: 'center'
            }}
            title={`${t('openRate')}:`}
          >
            <S.BarArea>
              <LinearProgressBar
                backgroundColor="var(--color-blue-100)"
                visualParts={[
                  {
                    percentage: rate.opened,
                    color: 'var(--color-blue-500)'
                  }
                ]}
              />
              <S.Rate>{rate.opened}</S.Rate>
            </S.BarArea>
          </DetailItem>
        </>
        <DetailItem
          bodyStyle={{
            display: 'flex',
            alignItems: 'center'
          }}
          title={`${t('acceptanceRate')}:`}
        >
          <S.BarArea>
            <LinearProgressBar
              backgroundColor="var(--color-green-100)"
              visualParts={[
                {
                  percentage: rate.accepted,
                  color: 'var(--color-green-500)'
                }
              ]}
            />
            <S.Rate>{rate.accepted}</S.Rate>
          </S.BarArea>
        </DetailItem>
      </S.StatusWrapper>

      <HorizontalDivider $margin="16px 0" />

      <S.CardFooter>
        {!isDraftOrReadyToSend(status) && !isContactLess(feature) && (
          <S.AvgTimeContainer>
            <Typography variant="body2Bold" color="fg" emphasis="weaker">
              {t('avgAcceptanceTime')}:
            </Typography>
            {averageAcceptanceRate ? (
              <Typography
                variant="body2Bold"
                gap={4}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                <Icon name="clock" />
                {averageAcceptanceRate}
              </Typography>
            ) : (
              <Typography variant="body2Bold" color="fg" emphasis="weaker">
                {t('notAcceptedYet')}
              </Typography>
            )}
          </S.AvgTimeContainer>
        )}

        <S.ButtonWrapper>
          {isLive(status) && (
            <Button
              size="sm"
              color="error"
              variant="outlined"
              onClick={onExpireModal}
              startIcon={<Icon name="slash" />}
              block
            >
              {t('endGift')}
            </Button>
          )}
          {(isExpired(status) || isDeactivate(status)) && (
            <Button
              size="sm"
              variant="outlined"
              color="secondary"
              onClick={onReactivateModal}
              loading={isCalculationLoading || isUpdatingRecognition}
              startIcon={<Icon name="gift-history" />}
              block
            >
              {t('reactivate')}
            </Button>
          )}
          {!isDeactivate(status) && !isTeams(feature) && !isApprovalPending(status) && (
            <Button
              size="sm"
              variant="outlined"
              onClick={() => {
                setDuplicateModalOpen(true);
              }}
              disabled={isCalculationLoading || isUpdatingRecognition}
              startIcon={<Icon name="copy-paste" />}
              block
            >
              {t('duplicate')}
            </Button>
          )}
          {isApprovalPending(status) && (
            <Button
              size="sm"
              variant="outlined"
              color="error"
              onClick={() => onCancelApproval()}
              startIcon={<Icon name="slash" />}
              block
            >
              {t('cancelApproval')}
            </Button>
          )}
        </S.ButtonWrapper>

        {(isDraftOrReadyToSend(status) || isExpired(status)) && (
          <S.ReactivateText>
            <Trans
              i18nKey="clickHereToArchive"
              components={{ button: <S.LinkButton onClick={onArchiveModal} /> }}
            />
          </S.ReactivateText>
        )}
      </S.CardFooter>

      <DuplicateModal
        isOpen={duplicateModalOpen}
        onOpen={setDuplicateModalOpen}
        onSubmit={onDuplicate}
        featureType={featureType}
        duplicateText={duplicateText}
        recipientCount={estimatedCharges?.recipientCount}
        giftCount={giftCount}
        contactType={contactType}
        giftMessage={giftMessage}
        giftPrice={estimatedCharges?.baseProductPrice}
        digitalGiftCard={digitalGiftCard}
      />
    </Card>
  );
}

StatusSummary.propTypes = {
  feature: PropTypes.shape({
    addons: PropTypes.array,
    code: PropTypes.string,
    products: PropTypes.array,
    numberOfContacts: PropTypes.number,
    status: PropTypes.shape({ current: PropTypes.string })
  }),
  spendingSummary: PropTypes.shape({
    adjustedEstimatedCharge: PropTypes.shape({ total: PropTypes.number })
  }),
  analytics: PropTypes.shape({ total: PropTypes.number, injected: PropTypes.number }),
  averageAcceptanceRate: PropTypes.number,
  onDuplicate: PropTypes.func,
  featureType: PropTypes.string,
  onArchive: PropTypes.func,
  onExpire: PropTypes.func,
  onReactivate: PropTypes.func,
  onCancelApproval: PropTypes.func,
  duplicateText: PropTypes.string,
  isCCPayment: PropTypes.bool,
  isUpdatingRecognition: PropTypes.bool,
  giftCount: PropTypes.number,
  contactType: PropTypes.string,
  giftMessage: PropTypes.string,
  setActiveTab: PropTypes.func
};

export { StatusSummary };

export const automationReactivateFormValidationSchema = Yup.object().shape({
  acceptanceDurationInHours: Yup.string().required()
});

export const recognitionReactivateFormValidationSchema = Yup.object().shape({
  expirationDate: Yup.date().min(new Date()).required()
});
